import React, { useState, useEffect } from 'react'
import { Modal, Button, message, InputNumber } from 'antd'
import { ThunderboltOutlined } from '@ant-design/icons'
import { ApiRequest } from '../../../../services/api/api'
import { modalStyles, titleStyle, buttonStyles, modalContainerStyle } from './modal-styles'

interface InspectionItemDetail {
  revAreaQty: number
  putAwayQty: number
}

interface PalletPreview {
  palletNumber: number
  quantity: number
}

interface PalletStackingInfo {
  palletStackingId: number
  palletStackingName: string
  quantity: number
  layoutImage: string
  layoutImageUrl: string
  ctnsPerLevel?: number
  level?: number
  addOnQty?: number
}

interface AutoSplitPalletModalProps {
  visible: boolean
  onClose: () => void
  poData: any
  itemData: any
  onSuccess: (palletData: any[], userPalletStacking: number) => void
}

const AutoSplitPalletModal: React.FC<AutoSplitPalletModalProps> = ({
  visible,
  onClose,
  poData,
  itemData,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [palletStacking, setPalletStacking] = useState<number>(0)
  const [userPalletStacking, setUserPalletStacking] = useState<number>(0)
  const [palletStackingInfo, setPalletStackingInfo] = useState<PalletStackingInfo | null>(null)
  const [palletPreview, setPalletPreview] = useState<PalletPreview[]>([])
  const [itemDetail, setItemDetail] = useState<InspectionItemDetail | null>(null)

  // 获取托盘堆叠规则
  const fetchPalletStacking = async () => {
    try {
      const rawMaterialResponse = await ApiRequest({
        url: `RawMaterial/GetRawMaterialById?id=${itemData.rawMaterial?.rawMaterialId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (rawMaterialResponse?.data?.data) {
        const rawMaterialData = rawMaterialResponse.data.data
        const productId = rawMaterialData.productId

        if (productId) {
          const productResponse = await ApiRequest({
            url: `Product/GetProductById?id=${productId}`,
            method: 'get',
            isShowSpinner: false
          })

          if (productResponse?.data?.data) {
            const product = productResponse.data.data
            if (product.palletStacking) {
              const palletStackingData = product.palletStacking
              const stackingQty = palletStackingData.quantity || 100
              setPalletStacking(stackingQty)

              let imageUrl = ''
              if (palletStackingData.layoutImage) {
                try {
                  const imageData = typeof palletStackingData.layoutImage === 'string'
                    ? JSON.parse(palletStackingData.layoutImage)
                    : palletStackingData.layoutImage
                  imageUrl = imageData.url || ''
                } catch (e) {
                  // Ignore JSON parse errors
                }
              }

              setPalletStackingInfo({
                palletStackingId: palletStackingData.palletStackingId,
                palletStackingName: palletStackingData.palletStackingName,
                quantity: palletStackingData.quantity,
                layoutImage: palletStackingData.layoutImage,
                layoutImageUrl: imageUrl,
                ctnsPerLevel: palletStackingData.ctnsPerLevel,
                level: palletStackingData.level,
                addOnQty: palletStackingData.addOnQty
              })
            }
          }
        }
      }
    } catch (error) {
      setPalletStacking(100)
    }
  }

  // 获取 Inspection Item 详情
  const fetchItemDetail = async () => {
    try {
      const inspectionResponse = await ApiRequest({
        url: `UnloadingInspection/GetAllUnloadingInspection?poId=${poData.poId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (inspectionResponse?.data?.data && inspectionResponse.data.data.length > 0) {
        const inspectionData = inspectionResponse.data.data[0]
        const foundDetail = (inspectionData.unloadingDetail || []).find(
          (detail: any) => detail.rawMaterialId === itemData.rawMaterial?.rawMaterialId
        )

        if (foundDetail) {
          setItemDetail({
            revAreaQty: foundDetail.revAreaQty || 0,
            putAwayQty: foundDetail.putAwayQty || 0
          })
        }
      }
    } catch (error) {
      setItemDetail(null)
    }
  }

  // 初始化数据
  useEffect(() => {
    if (visible && itemData) {
      fetchPalletStacking()
      fetchItemDetail()
    }
  }, [visible, itemData])

  // 初始化 userPalletStacking
  useEffect(() => {
    if (palletStacking > 0 && userPalletStacking === 0) {
      setUserPalletStacking(palletStacking)
    }
  }, [palletStacking])

  // 计算托盘分配预览
  useEffect(() => {
    if (visible && itemData) {
      calculateSplitPreview()
    }
  }, [visible, itemData, userPalletStacking, itemDetail])

  const calculateSplitPreview = () => {
    const revAreaQty = itemDetail?.revAreaQty ?? itemData?.totalRevQty ?? 0
    const stackingQty = userPalletStacking || palletStacking

    if (revAreaQty > 0 && stackingQty > 0) {
      const totalPallets = Math.ceil(revAreaQty / stackingQty)
      const preview: PalletPreview[] = []

      for (let i = 0; i < totalPallets; i++) {
        const remainingQty = revAreaQty - (i * stackingQty)
        const qty = Math.min(remainingQty, stackingQty)
        preview.push({
          palletNumber: i + 1,
          quantity: qty
        })
      }

      setPalletPreview(preview)
    } else {
      setPalletPreview([])
    }
  }

  const handleSplit = async () => {
    const stackingQty = userPalletStacking || palletStacking

    if (!stackingQty || stackingQty <= 0) {
      message.error('Invalid pallet stacking rule')
      return
    }

    const revAreaQty = itemDetail?.revAreaQty ?? itemData?.totalRevQty ?? 0

    if (revAreaQty <= 0) {
      message.error('This item has no receiving quantity and cannot be split')
      return
    }

    const palletData = palletPreview.map(pallet => ({
      quantity: pallet.quantity
    }))

    onSuccess(palletData, userPalletStacking)
  }

  const handleCancel = () => {
    setPalletStacking(0)
    setUserPalletStacking(0)
    setPalletStackingInfo(null)
    setPalletPreview([])
    onClose()
  }

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      width={900}
      footer={null}
      closable={true}
      style={{ top: 50 }}
      bodyStyle={{ padding: modalStyles.padding, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      <div style={modalContainerStyle}>
        {/* 页头标题 */}
        <div style={titleStyle}>
          Auto Split Pallets
        </div>

        {/* 物料信息表格 */}
        <table style={{ width: '100%', border: `1px solid ${modalStyles.borderSecondary}`, borderRadius: modalStyles.borderRadiusSmall, marginBottom: '16px' }}>
          <tbody>
            <tr>
              <td style={{ padding: '8px 12px', border: `1px solid ${modalStyles.borderSecondary}`, width: '25%', backgroundColor: modalStyles.bgGray }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: modalStyles.textSecondary }}>
                  Item Code:
                </span>
              </td>
              <td style={{ padding: '8px 12px', border: `1px solid ${modalStyles.borderSecondary}` }}>
                <span style={{ fontSize: '14px', color: modalStyles.textPrimary }}>
                  {itemData?.rawMaterial?.rawMaterialCode || 'N/A'}
                </span>
              </td>
              <td style={{ padding: '8px 12px', border: `1px solid ${modalStyles.borderSecondary}`, width: '25%', backgroundColor: modalStyles.bgGray }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: modalStyles.textSecondary }}>
                  Description:
                </span>
              </td>
              <td style={{ padding: '8px 12px', border: `1px solid ${modalStyles.borderSecondary}` }}>
                <span style={{ fontSize: '14px', color: modalStyles.textPrimary }}>
                  {itemData?.rawMaterial?.rawMaterialName || 'N/A'}
                </span>
              </td>
            </tr>
            <tr>
              <td style={{ padding: '8px 12px', border: `1px solid ${modalStyles.borderSecondary}`, backgroundColor: modalStyles.bgGray }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: modalStyles.textSecondary }}>
                  Receiving Qty:
                </span>
              </td>
              <td style={{ padding: '8px 12px', border: `1px solid ${modalStyles.borderSecondary}` }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: modalStyles.primary }}>
                  {itemDetail?.revAreaQty ?? itemData?.totalRevQty ?? 0}
                </span>
              </td>
              <td style={{ padding: '8px 12px', border: `1px solid ${modalStyles.borderSecondary}`, backgroundColor: modalStyles.bgGray }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: modalStyles.textSecondary }}>
                  Putaway Qty:
                </span>
              </td>
              <td style={{ padding: '8px 12px', border: `1px solid ${modalStyles.borderSecondary}` }}>
                <span style={{ fontSize: '14px', color: modalStyles.textPrimary }}>
                  {itemDetail?.putAwayQty ?? 0}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Pallet Stacking 和预览表格 */}
        <table style={{ width: '100%', border: `1px solid ${modalStyles.borderSecondary}`, borderRadius: modalStyles.borderRadiusSmall, marginBottom: '16px' }}>
          <tbody>
            <tr>
              <td style={{ padding: '12px', border: `1px solid ${modalStyles.borderSecondary}`, width: '30%', verticalAlign: 'top' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, color: modalStyles.textSecondary, marginBottom: '8px' }}>
                  Pallet Stacking:
                </div>
                {palletStackingInfo ? (
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: modalStyles.titleColor }}>
                      {palletStackingInfo.ctnsPerLevel && palletStackingInfo.level
                        ? `${palletStackingInfo.ctnsPerLevel}*${palletStackingInfo.level}+${palletStackingInfo.addOnQty || 0}`
                        : palletStackingInfo.palletStackingName || 'N/A'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <InputNumber
                        size="small"
                        min={1}
                        max={9999}
                        value={userPalletStacking || undefined}
                        placeholder={`${palletStacking}`}
                        onChange={(value) => {
                          const newValue = value !== null ? value : 0
                          if (newValue > 0) {
                            setUserPalletStacking(newValue)
                          } else {
                            setUserPalletStacking(palletStacking)
                          }
                        }}
                        style={{ width: '80px' }}
                        onPressEnter={(e) => e.stopPropagation()}
                      />
                      <span style={{ fontSize: '13px', color: modalStyles.textSecondary }}>
                        cartons/pallet
                      </span>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '14px', color: modalStyles.textSecondary }}>
                    Loading...
                  </div>
                )}
              </td>

              {/* Pallet Stacking 图片 */}
              <td style={{ padding: '12px', border: `1px solid ${modalStyles.borderSecondary}`, width: '20%', textAlign: 'center', verticalAlign: 'top' }}>
                {palletStackingInfo?.layoutImageUrl ? (
                  <img
                    src={palletStackingInfo.layoutImageUrl}
                    alt={palletStackingInfo.palletStackingName}
                    style={{
                      maxWidth: '60px',
                      maxHeight: '60px',
                      objectFit: 'contain',
                      border: `1px solid ${modalStyles.borderSecondary}`,
                      borderRadius: '6px',
                      backgroundColor: '#fff'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#e5e7eb',
                    border: `1px dashed ${modalStyles.borderSecondary}`,
                    borderRadius: '6px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: modalStyles.textSecondary,
                    fontSize: '9px',
                    textAlign: 'center',
                    margin: '0 auto'
                  }}>
                    No Image
                  </div>
                )}
              </td>

              {/* Pallet Preview */}
              <td style={{ padding: '12px', border: `1px solid ${modalStyles.borderSecondary}`, verticalAlign: 'top' }}>
                <div style={{ fontSize: '14px', fontWeight: 500, color: modalStyles.textSecondary, marginBottom: '8px' }}>
                  Pallet Preview ({palletPreview.length} pallets):
                </div>
                {palletPreview.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {palletPreview.map((pallet, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: modalStyles.bgLight,
                          border: `1px solid ${modalStyles.borderSecondary}`,
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: modalStyles.primary
                        }}
                      >
                        Pallet {pallet.palletNumber}: {pallet.quantity}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '14px', color: modalStyles.textSecondary }}>
                    No pallet preview available
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/* 底部按钮 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: `2px solid ${modalStyles.borderSecondary}` }}>
          <Button
            onClick={handleCancel}
            style={{
              ...buttonStyles.secondary,
              minWidth: '120px',
              lineHeight: '1.5',
              padding: '8px 20px',
              fontSize: '14px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSplit}
            loading={loading}
            disabled={palletPreview.length === 0}
            style={{
              ...buttonStyles.info,
              minWidth: '120px',
              lineHeight: '1.5',
              padding: '8px 20px',
              fontSize: '14px',
              position: 'relative'
            }}
          >
            Split
            <ThunderboltOutlined style={{
              position: 'absolute',
              top: '4px',
              right: '8px',
              fontSize: '13px',
              color: '#93c5fd'
            }} />
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AutoSplitPalletModal
